<script lang="ts" setup>   
    import '../../css/choice.css';
    import {onMounted,computed,reactive,ref} from 'vue';
    import {interactionType,getSeverityClass,severityLevel,printClass,reportSectionBreak} from '../lib/Models/InteractionModel';
    import ModalDialog from "../components/ModalDialog.vue";
    import {ModalSize,ButtonDisplaySize,ButtonDisplayStyle} from "../Enums/UiEnums"; 
    import DrugInteractions from "../components/DrugInteractions.vue";
    import AllergyInteractions from "../components/AllergyInteractions.vue";
    import DiseaseInteractions from "../components/DiseaseInteractions.vue";
    import MedFDBDisclaimer from "../components/MedFDBDisclaimer.vue";
    import ActionButtonV2 from '../components/ActionButtonV2.vue';
    import {ActionButtonStyle} from '../lib/Types/Types';
    import {openWindowFromVue} from '../lib/Models/CommonItemsModel';    

    const rxReportField2 = ref(); 
    const formData = reactive <
    {   
        interactionRetrieved: boolean,
        inputs: {prescriptionID: number, medicationID: number, medicationLevel: number}, 
        reportData: {patient?: string, disclaimer?: string, allergy?: string, drug?: string, disease?: string},        
        fields: {title: string, drugName: string, minSeverityLevel: number, interactionCount: number}
    }>({     
        interactionRetrieved : false,
        inputs: {prescriptionID: 0, medicationID: 0, medicationLevel: 0},
        reportData: {patient: "", disclaimer: "", allergy: "", drug: "", disease: ""},
        fields: {title: "", drugName: "", minSeverityLevel: severityLevel.NoneFound, interactionCount: 0}
    });
    onMounted(() => { 
        formData.inputs = {prescriptionID: props.prescriptionID, medicationID: props.medicationID, medicationLevel: props.medicationLevel}; 
    });
    const props = withDefaults( defineProps<
        {
            showDialog: boolean,
            patientID: number,
            prescriptionID?: number,
            medicationID?: number,
            medicationLevel?: number,
            interactionType: interactionType
        }>(),
        {
            showDialog: false,
            prescriptionID: 0,
            medicationID: 0,
            medicationLevel: 0
        });

    const emit=defineEmits(["closeInformation"]);  
    
    const reportID = computed<string>(() => "reportID2" + "_" + props.interactionType + "_" + ((props.prescriptionID !== 0 ? props.prescriptionID : props.medicationID) ?? 0));

    function updateModal(drugName : string,minSeverityLevel : severityLevel, interactionCount : number) : void {
        formData.fields.title = `${interactionType[props.interactionType]} Interactions ${(interactionCount > 1 ? "(" + interactionCount + ")" : "")} for:`;
        formData.fields.drugName = drugName;
        formData.fields.minSeverityLevel = minSeverityLevel;
        formData.fields.interactionCount = interactionCount;
    } 
    function updateModalDisclaimer(success: boolean, patientData:  string, disclaimer: string) :  void { 
        if (success) {
            formData.reportData.patient = patientData;
            formData.reportData.disclaimer = disclaimer;
        } 
    }
    function updateModalDrug(success: boolean, drugData: string) :  void { 
        if (success) { 
            formData.reportData.drug = drugData;
        } 
    }
    function updateModalDisease(success: boolean, diseaseData: string) :  void { 
        if (success) { 
            formData.reportData.disease = diseaseData;
        } 
    }
    function updateModalAllergy(success: boolean, allergyData: string) :  void { 
        if (success) { 
            formData.reportData.allergy = allergyData;
        } 
    } 
    async function CloseDialog() : Promise<void> {   
        emit('closeInformation');
    } 
    async function printInteractions() : Promise<void> {
        switch (props.interactionType) {
            case interactionType.Allergy: 
                printReport(formData.reportData.allergy); 
                break;
            case interactionType.Disease:
                printReport(formData.reportData.disease); 
                break;
            case interactionType.Drug:
                printReport(formData.reportData.drug); 
                break;
        } 
    }
    function printReport(report_data : string | undefined) {
        if (!report_data) {
            return;
        }
        const report_header = "Drug Information";
        const reportTitle = 
            `<div style="${printClass.reportTitle}">${formData.fields.title}</div>` +
            `<div style="${printClass.reportDrugName}">${formData.fields.drugName}</div>`;
            
        rxReportField2.value.innerHTML = 
                reportTitle + 
                reportSectionBreak +
                formData.reportData.patient + 
                formData.reportData.disclaimer + 
                reportSectionBreak +
                report_data;
        const pdf_url = `/officemd/datapages/pdf_report.asp?txtReportSection=HTML&txtReportField=${rxReportField2.value.id}&PID=${props.patientID}&DIS=${report_header}`;
        openWindowFromVue("PDFWIndow", pdf_url,{ noTimestamp: "Y" }); 
    } 
</script>

<template>
    <ModalDialog :size=ModalSize.Large :show="props.showDialog" :closeOnClickOutside="false" @hidden="CloseDialog"> 
        <template v-slot:title>
            <div class="fw-bold fs-6 text-isalus-primary">{{ formData.fields.title }}</div>
            <em  v-show="formData.fields.minSeverityLevel ?? severityLevel.None > severityLevel.None" :class="getSeverityClass(formData.fields.minSeverityLevel)" :title="`Interaction Level ${formData.fields.minSeverityLevel}`"></em>
            <span class="fw-bold fs-6 text-secondary ps-2">{{formData.fields.drugName}}</span>
        </template>
        <template #footer>
            <div class="row container-fluid">
                <div class="col text-start">
                    <MedFDBDisclaimer 
                        :showDialog="props.showDialog"
                        :patientID="props.patientID"    
                        :prescriptionID="props.prescriptionID"
                        :medicationID="props.medicationID"
                        @updateModalDisclaimer="updateModalDisclaimer">
                    </MedFDBDisclaimer>
                </div>
            </div>
            <div class="row container-fluid">
                <div class="col text-end">
                    <span class="pe-2">
                        <ActionButtonV2 
                            :action="ActionButtonStyle.Print"  
                            :text="'Print'"  
                            :tooltip="'Print Interactions'"  
                            :size="ButtonDisplaySize.Small" 
                            :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                            :performAction="printInteractions" 
                            :disabled="false" />
                    </span>
                    <span class="pe-2">
                        <ActionButtonV2 
                            :action="ActionButtonStyle.Close"  
                            :text="'Close'"  
                            :tooltip="'Close Interactions'"  
                            :size="ButtonDisplaySize.Small" 
                            :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                            :performAction="CloseDialog" 
                            :disabled="false" />
                    </span>
                </div>
            </div>   
        </template>
        <div>
            <div class="row pt-2">  
                <div v-if="props.interactionType === interactionType.Drug" class="col"> 
                    <DrugInteractions 
                        :showDialog="props.showDialog"
                        :patientID="props.patientID"    
                        :prescriptionID="props.prescriptionID"
                        :medicationID="props.medicationID"
                        :medicationLevel="props.medicationLevel"
                        :interactionType="interactionType.Drug"
                        @updateModal="updateModal"
                        @updateModalDrug="updateModalDrug">
                    </DrugInteractions>
                </div>
                <div v-if="props.interactionType === interactionType.Allergy" class="col"> 
                    <AllergyInteractions 
                        :showDialog="props.showDialog"
                        :patientID="props.patientID"    
                        :prescriptionID="props.prescriptionID"
                        :medicationID="props.medicationID"
                        :medicationLevel="props.medicationLevel"
                        :interactionType="interactionType.Allergy"
                        @updateModal="updateModal"
                        @updateModalAllergy="updateModalAllergy">
                    </AllergyInteractions>
                </div>
                <div v-if="props.interactionType === interactionType.Disease" class="col"> 
                    <DiseaseInteractions 
                        :showDialog="props.showDialog"
                        :patientID="props.patientID"    
                        :prescriptionID="props.prescriptionID"
                        :medicationID="props.medicationID"
                        :medicationLevel="props.medicationLevel"
                        :interactionType="interactionType.Disease"
                        @updateModal="updateModal"
                        @updateModalDisease="updateModalDisease">
                    </DiseaseInteractions>
                </div>
            </div>
        </div>  
        
        <div ref="rxReportField2" :id="reportID" style="display:none"></div>
    </ModalDialog>
</template>